/**
 * =====================================================
 * TALAPP-1841 — Reset de scroll en la navegación SPA
 * =====================================================
 * wouter NO resetea el scroll al cambiar de ruta (a diferencia de
 * react-router) y en ava-client no existía ningún reset global: el
 * `window.scrollY` de la pantalla anterior se conservaba íntegro.
 *
 * Se manifestaba en /review porque es la pantalla más alta del wizard, se
 * llega a ella desde el PIE de pantallas largas (Beneficiarios, Documentos)
 * y tanto su chunk lazy como la query `onboarding.get` llegan cacheados:
 * sin skeleton que colapse el scroll, el primer paint ya es la página
 * completa heredando el offset. El defecto era latente en TODAS las
 * pantallas — por eso el fix es global y no por pantalla.
 *
 * `useLayoutEffect` y no `useEffect`: corre después de mutar el DOM y ANTES
 * del paint, así el frame con el scroll heredado nunca llega a dibujarse.
 * Con `useEffect` (passive) el reset ocurre después del primer paint y el
 * usuario ve el salto — justamente el artefacto que el ticket elimina.
 * El costo es nulo: `scrollTo` no invalida el layout, solo recompone.
 * =====================================================
 */
import { useLayoutEffect } from "react";
import { useLocation } from "wouter";

/**
 * Lleva el scroll del documento al tope, sin animación.
 *
 * Afecta SOLO al scroller del documento. Los contenedores con scroll propio
 * —el rail de /review (`ReviewRail`, `overflow-y-auto`), los dropdowns, los
 * dialogs— quedan intactos.
 */
export function resetScrollTop(): void {
  if (typeof window === "undefined") return;
  if (typeof window.scrollTo !== "function") return;
  window.scrollTo(0, 0);
}

/**
 * Desactiva la restauración de scroll del navegador (F5 y back/forward).
 *
 * Sin esto, un F5 sobre /review scrolleado vuelve a abrir al final: la
 * restauración del browser ocurre antes de que React monte y el reset por
 * ruta no la pisa. Debe invocarse ANTES del primer render.
 */
export function disableBrowserScrollRestoration(): void {
  if (typeof window === "undefined") return;
  if (!window.history || !("scrollRestoration" in window.history)) return;
  window.history.scrollRestoration = "manual";
}

/**
 * Resetea el scroll en cada cambio de ruta.
 *
 * `useLocation` de wouter devuelve solo el pathname, así que el efecto no se
 * dispara por cambios de query string ni por navegar a la misma ruta.
 */
export function useScrollToTop(): void {
  const [location] = useLocation();

  useLayoutEffect(() => {
    resetScrollTop();
  }, [location]);
}
